import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";

@Injectable({ providedIn: "root" })
export class LoaderService {
  private readonly showLoaderSource = new BehaviorSubject<boolean>(false);
  public readonly showLoader$: Observable<boolean> =
    this.showLoaderSource.asObservable();

  /**
   * Tuning knobs:
   * - showDelayMs: how long a request must be "busy" before showing the bar
   * - minVisibleMs: once shown, keep visible this long to avoid blink
   */
  private readonly showDelayMs = 120; // shows for real waits, suppresses tiny flickers
  private readonly minVisibleMs = 300;

  private showTimer: ReturnType<typeof setTimeout> | null = null;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;
  private visibleSince = 0;

  displayLoader(show: boolean): void {
    // Cancel pending hide if a new "show" comes in
    if (show && this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }

    // Cancel pending show if we're asked to hide
    if (!show && this.showTimer) {
      clearTimeout(this.showTimer);
      this.showTimer = null;
    }

    const currentlyVisible = this.showLoaderSource.value;

    if (show) {
      if (currentlyVisible) return;

      // Debounce show: only show if busy persists
      if (!this.showTimer) {
        this.showTimer = setTimeout(() => {
          this.showTimer = null;
          this.visibleSince = Date.now();
          this.showLoaderSource.next(true);
        }, this.showDelayMs);
      }
      return;
    }

    // Hide
    if (!currentlyVisible) return;

    const elapsed = Date.now() - this.visibleSince;
    const wait = Math.max(0, this.minVisibleMs - elapsed);

    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
    }

    this.hideTimer = setTimeout(() => {
      this.hideTimer = null;
      this.showLoaderSource.next(false);
    }, wait);
  }
}
